<template>
  <div id="donate-form" class="wwf-form donation-form">
    <!-- LOADING -->
    <div class="wwf-overlay loading" v-show="donation.isLoading">
      <div class="wwf-loading">
        <span></span>
      </div>
    </div>

    <!-- Failed -->
    <div class="wwf-overlay processing" v-cloak v-show="donation.isFailed">
      <div class="processing-title">
        Unable to process donation, please try again
      </div>
    </div>

    <!-- Form -->
    <Stepper
      :thankYouUrl="thankYouUrl"
      :useProductThankYouUrl="useProductThankYouUrl"
      :blacklistUrl="blacklistUrl"
      :showBequest="showBequest"
      :showInLovingMemory="showInLovingMemory"
      :customOnLeft="customOnLeft"
      :twoDollarLink="twoDollarLink"
      :donateOnceLink="donateOnceLink"
      :defaultCustom="defaultCustom"
      :marketingEmail="marketingEmail"
      :showPhone="showPhone"
      :isPhoneMandatory="isPhoneMandatory"
      @reset="reset"
    ></Stepper>
  </div>
</template>

<script>
import Stepper from "./Stepper.vue";
import {
  getParameterByName,
  getNameByDonationType,
  getNameByPaymentType,
  removeURLParameter,
  getUtmParams,
} from "../../helpers";
import gtm from "../../utils/gtm";
import slack from "../../utils/slack";
import stringify from "fast-safe-stringify";

import { mapState } from "vuex";

export default {
  name: "Donate",
  components: {
    Stepper,
  },

  props: {
    productId: {
      type: String,
      required: true,
    },
    showInLovingMemory: {
      type: Boolean,
      default: false,
    },
    thankYouUrl: {
      type: String,
      default: "https://wwf.org.au/donate/thank-you-for-helping/",
    },
    useProductThankYouUrl: {
      type: Boolean,
      default: false,
    },
    blacklistUrl: {
      type: String,
    },
    showBequest: {
      type: Boolean,
      default: true,
    },
    customOnLeft: {
      type: Boolean,
      default: false,
    },
    twoDollarLink: {
      type: String,
    },
    donateOnceLink: {
      type: String,
    },
    defaultCustom: {
      type: Boolean,
      default: false,
    },
    marketingEmail: {
      type: Boolean,
      default: false,
    },
    showPhone: {
      type: Boolean,
      default: true,
    },
    isPhoneMandatory: {
      type: Boolean,
      default: true,
    },
  },
  computed: mapState({
    donation: (state) => state.donation.donation,
    product: (state) => state.product.product,
  }),

  created: async function () {
    this.setLoading(true);

    // Get source 
    let source = window.top.location.href;
    // If there is no utm params at the top url && persisted utm exists from the iframe url 
    if (getUtmParams(source).length === 0 && getUtmParams(window.location.href).length > 0) {
      // Add the persisted utm param 
      const url = window.top.location.href.split("?")[0];
      source = url + getUtmParams(window.location.href);
      
      // Add the rest of the params from top url back in if exists 
      const params = window.top.location.href.split("?")[1];
      if (params) {
        source += `&${params}`;
      }
    }
    
    // Fetch product informations
    // We can have query params to override the appeal and fund id
    await this.$store.dispatch("getProductById", {
      id: this.productId,
      source,
      appealid: getParameterByName("t"),
      fundid: getParameterByName("f"),
    });

    await this.checkPaypalCallback();

    // Set default donation type by query param
    const dt = getParameterByName("dt", window.top.location.href);
    if (dt && (dt === "Single" || dt === "Regular")) {
      this.defaultDonationType = dt;
    }

    // DONATION TYPE - Pre-select the dollar handle and the package (rule: first item on the list)
    // As we only have Single and Regular, always set Single as the first option
    const defaultTypeExists =
      this.product.donationtypes.filter((e) => e === this.defaultDonationType)
        .length === 1;
    let defaultType =
      defaultTypeExists && this.defaultDonationType
        ? this.defaultDonationType
        : this.product.appealcodesetting.defaultdonationtype;

    // Reverse alphabetical order
    this.product.donationtypes = this.product.donationtypes.sort((a, b) => {
      if (a === defaultType) {
        return -1;
      }
      if (b === defaultType) {
        return 1;
      }
      return 0;
    });

    this.donation.type = defaultType;
    const settings = this.product.donationsettings[defaultType];
    if (settings) {
      this.donation.campaignid = settings.campaignid;
      this.donation.fundid = settings.fundid;
    }

    // DOLLAR HANDLE - Select first element
    const handles = this.product.dollarhandles[defaultType];
    if (handles) {
      this.donation.amount = handles[0].amount;
      this.donation.dollarhandle = handles[0].amount;
    }

    if (this.product.producttype != "wildcard") {
      // Pre-select the package (rule: first item on the list)
      this.donation.packagecode =
        this.product.packages && this.product.packages.length > 0
          ? this.product.packages[0]
          : null;
      await this.$store.dispatch("getAllPackagesForProduct");
    }

    // PURL: Check if there are some query string in URL to prefill the form, Query from donate widget
    // TODO: check the following to get the parent url ????? maybe
    const q = getParameterByName("q", window.top.location.href);
    if (q) {
      let paramsPrefill = "?" + window.atob(q);
      console.log("paramsPrefill", paramsPrefill);
      let amount = getParameterByName("amount", paramsPrefill);
      let email = getParameterByName("email", paramsPrefill);
      let firstname = getParameterByName("firstname", paramsPrefill);
      let lastname = getParameterByName("lastname", paramsPrefill);
      let salutation = getParameterByName("salutation", paramsPrefill);
      let wildcardCode = getParameterByName("wildcardCode", paramsPrefill);
      if (wildcardCode) {
        this.donation.packagecode = wildcardCode;
      }
      let dollarhandle = getParameterByName("dollarhandle", paramsPrefill)
        ? getParameterByName("dollarhandle", paramsPrefill).replace("$", "")
        : undefined;

      // URL dollar handles (single)
      let dollarhandles = getParameterByName("dollarhandles", paramsPrefill);
      if (dollarhandles) {
        // get dollarhandles from query params
        dollarhandles = dollarhandles.replace(/[^0-9\. ]+/g, "").split(" ");
        let newDollarHandles = [];
        dollarhandles.forEach((v, index) => {
          // Check if there is a product dollar handles index
          if (this.product.dollarhandles.Single[index]) {
            newDollarHandles.push({
              amount: v,
              img: this.product.dollarhandles.Single[index].img,
              text: this.product.dollarhandles.Single[index].text,
            });
          }
        });
        this.product.dollarhandles.Single = newDollarHandles;
      }

      // URL dollar handles (regular)
      dollarhandles = getParameterByName("regulardollarhandles", paramsPrefill);
      if (dollarhandles) {
        // get dollarhandles from query params
        dollarhandles = dollarhandles.replace(/[^0-9\. ]+/g, "").split(" ");
        let newDollarHandles = [];
        dollarhandles.forEach((v, index) => {
          // Check if there is a product dollar handles index
          if (this.product.dollarhandles.Regular[index]) {
            newDollarHandles.push({
              amount: v,
              img: this.product.dollarhandles.Regular[index].img,
              text: this.product.dollarhandles.Regular[index].text,
            });
          }
        });
        this.product.dollarhandles.Regular = newDollarHandles;
      }

      this.donation.dollarhandle = dollarhandle;
      this.donation.amount = amount;
      this.donation.email = email;
      this.donation.firstname = firstname;
      this.donation.lastname = lastname;
      this.donation.title = salutation;
    }

    // GTM: Push datalayer add to cart
    gtm.pushProductDetail(
      this.product.productname,
      this.product.id,
      this.product.producttype,
      getNameByDonationType(this.donation.type)
    );

    // Now handled by Maker's Tech
    // this.$store.dispatch("logProductVisit");

    this.setLoading(false);
  },
  methods: {
    async checkPaypalCallback() {
      // Wait for a second for recaptcha script to load
      await new Promise((resolve) => setTimeout(resolve, 1000));

      let timeout = null;
      try {
        // Check if there payment paypal pending to excecute
        const paymentId = getParameterByName("token", window.top.location.href);
        const payerId = getParameterByName("PayerID", window.top.location.href);
        if (payerId && paymentId) {
          console.log("Paypal Callback - Payment found");

          // Add a timeout for 30s
          timeout = setTimeout(this.paypalFailed, 30000);
          const donation = await this.$store.dispatch("paymentPaypal", {
            payerid: payerId,
            paymentid: paymentId,
          });

          // Clear the timeout if the payment was done within 30s
          clearTimeout(timeout);
          this.donation.amount = donation.amount;
          console.log("Paypal Callback - Donaion", donation);
          this.$store.dispatch("logFormPaySuccess", donation.key);

          if (donation) {
            // Remove the query params from the URL
            let querylessUrl = removeURLParameter(
              "token",
              window.top.location.href
            );
            querylessUrl = removeURLParameter("payerId", querylessUrl);
            window.top.history.pushState({}, document.title, querylessUrl);
            this.thanksUser(donation);
            this.reset();
          }
        }
      } catch (err) {
        // execute timeout now as donation failed
        clearTimeout(timeout);
        this.paypalFailed();
        slack.alert(`Paypal error ${stringify(err, null, 4)}`);
        console.error("Paypal Callback - error", err);
      }
    },
    paypalFailed() {
      // Remove the Processing screen
      this.donation.isProcessing = false;
      this.donation.isFailed = true;

      // Redirect users back to the page after 3s
      setTimeout(() => {
        this.donation.isFailed = false;
        window.top.location.href = window.top.location.href.split("?")[0];
      }, 3000);
    },
    thanksUser(donation) {
      this.$store.dispatch("logDonationComplete", donation);

      // Continue with regular thank you flow
      let url = this.thankYouUrl;

      // If useProductThankYou is set to true, we use url from product instead
      const donateThankYouUrl = this.donation.thankYouUrl;
      if (this.useProductThankYouUrl && donateThankYouUrl) {
        let query = `firstname=${this.donation.firstname}`;
        query += `&lastname=${this.donation.lastname}`;
        query += `&frequency=${this.donation.type}`;
        query += `&amount=${this.donation.amount}`;
        query += `&source=${document.URL}`;

        let params = "?q=" + window.btoa(query);
        url = donateThankYouUrl + params;
      }

      // Send data to Google Tag Manager
      gtm.pushPurchase(
        donation.key,
        donation.productname,
        donation.productid,
        donation.producttype,
        getNameByDonationType(donation.type),
        donation.amount,
        donation.postcode,
        getNameByPaymentType("wwf_paypal"),
        function () {
          setTimeout(() => {
            console.log("redirecting to thank you");
            window.top.location.href = url;
          }, 2000);
        }
      );
    },
    setLoading(value) {
      this.donation.isLoading = value;
    },
    async reset() {
      this.$store.dispatch("resetDonation");

      const defaultTypeExists =
        this.product.donationtypes.filter((e) => e === this.defaultDonationType)
          .length === 1;
      let defaultType =
        defaultTypeExists && this.defaultDonationType
          ? this.defaultDonationType
          : this.product.appealcodesetting.defaultdonationtype;

      this.donation.type = defaultType;
      const settings = this.product.donationsettings[defaultType];
      if (settings) {
        this.donation.campaignid = settings.campaignid;
        this.donation.fundid = settings.fundid;
      }

      if (this.product.producttype != "wildcard") {
        // Pre-select the package (rule: first item on the list)
        this.donation.packagecode =
          this.product.packages && this.product.packages.length > 0
            ? this.product.packages[0]
            : null;
        await this.$store.dispatch("getAllPackagesForProduct");
      }

      console.log("Reset donate form");
    },
  },
};
</script>

<style scoped>
/* From elcom, integrated for Makers-tech */
h1,
h2,
h3 {
  font-family: "WWF", sans-serif;
  position: relative;
  z-index: 1;
}
/* END */

/* WWF LOADER */
.wwf-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.94);
  z-index: 99999;
  overflow: hidden;
  border-radius: 3px;
}

.wwf-form .wwf-overlay.loading:before {
  content: "Just a sec...";
  position: absolute;
  top: 50%;
  text-align: center;
  left: 0;
  right: 0;
  margin: 70px auto 0;
  font-weight: 600;
}

.wwf-form.loading {
  min-height: 450px;
}

.wwf-form.loading > *:not(.wwf-loading),
.wwf-form.loading *:not(span) {
  visibility: hidden;
  opacity: 0;
}

.wwf-form.loading .wwf-loading {
  visibility: visible;
  opacity: 1;
  display: block;
}

.wwf-loading {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  left: 0;
  right: 0;
  margin: -25px auto 0;
  top: 50%;
  z-index: 9;
  font-size: 0;
}

.wwf-loading span,
.wwf-loading:before,
.wwf-loading:after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: block;
  font-size: 0;
}

.wwf-loading span {
  z-index: 3;
  background-image: url(https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/4796/dist/assets/img/wwf-logo-solo.svg);
  background-repeat: no-repeat;
  background-size: 50%;
  background-position: 50%;
  animation: wwfLogoLoading 1s linear infinite;
}

.wwf-loading:before {
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.03);
  animation: wwfLoading 1s linear infinite;
}

.wwf-loading:after {
  z-index: 1;
  /*background-color: rgba(0, 0, 0, 0.1);*/
  /*animation: wwfLoading 1s .6s linear infinite;*/
}

.iframe-loader .wwf-loading {
  display: block !important;
}

@keyframes wwfLogoLoading {
  0% {
    transform: scale(0.9);
  }
  10% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.9);
  }
}

@keyframes wwfLoading {
  0% {
    transform: scale(1);
    opacity: 0;
  }
  10% {
    transform: scale(1);
    opacity: 1;
  }
  70% {
    transform: scale(1.25);
    opacity: 1;
  }
  100% {
    transform: scale(1.25);
    opacity: 0;
  }
}

/* PROCESSING OVERLAY */
.processing {
  position: fixed;
  border-radius: 0;
}

.processing-title {
  position: absolute;
  left: 0;
  right: 0;
  margin: 70px auto 0;
  top: 50%;
  text-align: center;
  text-transform: uppercase;
  font-weight: 600;
}

/* Override Makers Tech's body style */
.donation-form {
  background: white;
}
/* END */
</style>


// WEBPACK FOOTER //
// Donate.vue?17de68ed